import {USER_KEYS} from "@/api/userApi";
import TickIcon from "@/assets/colorable/tick.svg?react";
import Loader from "@/components/Common/kpi/Loader";
import {client} from "@/httpClient";
import RegistrationInputField from "@/pages/registerCompany/RegistrationInputField";
import useRegistrationStore, {EmailSchema} from "@/store/registration";
import {useMutation} from "@tanstack/react-query";
import {type FC, useEffect, useState} from "react";
import {useTranslation} from "react-i18next";
import {useDebounce} from "react-use";

const RegistrationEmailField: FC<{className?: string; labelClassName?: string}> = ({className, labelClassName}) => {
  const {t} = useTranslation("common");
  const email = useRegistrationStore(state => state.email);
  const isPrefilled = useRegistrationStore(state => state.isEmailPrefilled);
  const isEmailValidated = useRegistrationStore(state => state.isEmailValidated);

  const setValue = useRegistrationStore(state => state.setValue);
  const [emailState, setEmailState] = useState(email);

  useEffect(() => {
    if (isPrefilled) {
      setValue("isEmailValidated", true);
    }
  }, [isPrefilled, setValue]);

  useDebounce(
    () => {
      if (isPrefilled) return;

      setValue("email", emailState);
      if (emailState !== "" && validateSchema(EmailSchema)) {
        checkIfEmailExists(emailState);
      }
    },
    1000,
    [emailState],
  );
  const setError = useRegistrationStore(state => state.setError);
  const validateSchema = useRegistrationStore(state => state.validateSchema);
  const {mutate: checkIfEmailExists, isPending} = useMutation({
    meta: {skipErrorToast: true, skipSuccessToast: true},
    mutationKey: USER_KEYS.mUserEmail(email),
    mutationFn: (email: string) => client.get(USER_KEYS.mUserEmail(email)[0]),
    onSuccess: response => {
      setValue("isEmailValidated", !response.data.exists);
      setError("email", response.data.exists ? "emailAlreadyExists" : "");
    },
  });

  return (
    <RegistrationInputField
      storeKey="email"
      className={className}
      label={t("email")}
      labelClassName={labelClassName}
      placeholder={t("yourEmailAddress")}
      type="email"
      disabled={isPrefilled}
      value={isPrefilled ? email : emailState}
      onChange={value => {
        setEmailState(value);
        setValue("isEmailValidated", false);
        setError("email", "");
      }}
      icon={
        isPrefilled ? null : isEmailValidated ? (
          <div className={"absolute top-0 right-0 bottom-0 mr-3 flex h-full items-center justify-center p-2"}>
            <TickIcon className="border-success-light-green bg-success-herbal size-6 rounded-full border p-1 text-white" />
          </div>
        ) : (
          isPending && (
            <Loader
              styles={{
                loaderClassName: "size-5.5",
                containerClassName:
                  "absolute bottom-0 right-0 top-0 h-full pr-5 pl-1.5 flex items-center justify-center",
              }}
            />
          )
        )
      }
    />
  );
};

export default RegistrationEmailField;
